// WebRTC peer helper for the ecom live shopping-assist voice call (browser, plain WebRTC,
// no third-party SDK). Roles:
//   - "caller" (agent, here): creates the offer once the shopper accepts.
//   - "callee" (storefront shopper): answers.
// The socket layer relays SDP/ICE and supplies the TURN/STUN `iceServers`; media is P2P.
//
// ICE candidates can arrive before the remote description is set, so they're buffered and
// flushed once the description lands — otherwise addIceCandidate throws and audio silently
// fails to connect.

const MIC_CONSTRAINTS = {
  audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  video: false,
};

// True when the browser can actually do a voice call (secure context + getUserMedia + RTC).
export function callSupported() {
  return !!(
    typeof RTCPeerConnection !== "undefined" &&
    navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia
  );
}

export function createVoicePeer({ role, iceServers, onIce, onRemoteStream, onState }) {
  const pc = new RTCPeerConnection({ iceServers: iceServers || [] });
  let localStream = null;
  let hasRemote = false;
  const pendingIce = [];

  pc.onicecandidate = (e) => {
    if (e.candidate && onIce) onIce(e.candidate.toJSON ? e.candidate.toJSON() : e.candidate);
  };
  pc.ontrack = (e) => {
    const stream = e.streams && e.streams[0];
    if (stream && onRemoteStream) onRemoteStream(stream);
  };
  pc.onconnectionstatechange = () => onState && onState(pc.connectionState);

  async function captureMic() {
    localStream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
    localStream.getTracks().forEach((t) => pc.addTrack(t, localStream));
  }

  async function flushPendingRemote(desc) {
    await pc.setRemoteDescription(new RTCSessionDescription(desc));
    hasRemote = true;
    while (pendingIce.length) {
      const c = pendingIce.shift();
      try {
        await pc.addIceCandidate(new RTCIceCandidate(c));
      } catch (e) {
        /* a late/duplicate candidate is non-fatal */
      }
    }
  }

  return {
    // caller: build + return the SDP offer (after grabbing the mic).
    async createOffer() {
      await captureMic();
      const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
      await pc.setLocalDescription(offer);
      return { type: offer.type, sdp: offer.sdp };
    },
    // caller: apply the callee's answer.
    async acceptAnswer(sdp) {
      await flushPendingRemote(sdp);
    },
    // callee: apply the offer, grab the mic, build + return the SDP answer.
    async acceptOffer(sdp) {
      await flushPendingRemote(sdp);
      await captureMic();
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      return { type: answer.type, sdp: answer.sdp };
    },
    addIce(candidate) {
      if (!candidate) return;
      if (!hasRemote) {
        pendingIce.push(candidate);
        return;
      }
      pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
    },
    setMuted(muted) {
      if (localStream) localStream.getAudioTracks().forEach((t) => (t.enabled = !muted));
    },
    close() {
      try {
        if (localStream) localStream.getTracks().forEach((t) => t.stop());
      } catch (e) {
        /* noop */
      }
      try {
        pc.onicecandidate = null;
        pc.ontrack = null;
        pc.onconnectionstatechange = null;
        pc.close();
      } catch (e) {
        /* noop */
      }
    },
  };
}
